import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Mail, MapPin, Building2, Phone, MessageSquare } from 'lucide-react';
import ContactSection from '../components/ContactSection';
import { usePageMeta } from '../hooks/usePageMeta';

const Yhteys = () => {
  const { language } = useLanguage();
  usePageMeta({
    title: 'Agencive – Varaa maksuton kartoitus',
    description: 'Ota yhteyttä ja varaa maksuton kartoitus brändiagentista, automaatioista tai sisällöstä.',
    path: '/yhteys',
  });

  return (
    <main className="min-h-screen bg-navy text-white">
      {/* Hero Section */}
      <section className="py-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal/10 via-transparent to-navy"></div>
        
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-teal/20 rounded-full mb-6">
            <MessageSquare className="w-4 h-4 text-teal" />
            <span className="text-teal text-sm font-semibold">Yhteydenotto</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white">
            {language === 'fi' ? 'Ota yhteyttä' : 'Contact Us'}
          </h1>
          
          <p className="text-xl text-white/70 leading-relaxed">
            {language === 'fi'
              ? 'Oletko kiinnostunut brändiagentista tai sitä tukevista automaatioista? Ota yhteyttä, niin arvioimme sopivan käyttötapauksen ja toteutustavan.'
              : "Interested in a brand agent or supporting automations? Get in touch and we'll evaluate the best use case and approach."
            }
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
          
          {/* Contact Information */}
          <div>
            <h2 className="text-3xl font-bold mb-8 text-white">Yhteystiedot</h2>
            
            <div className="space-y-6">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <div className="flex items-start space-x-4">
                  <Building2 className="w-8 h-8 text-teal flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-white mb-2">Yritys</h3>
                    <p className="text-white/70">Agencive AI Oy</p>
                    <p className="text-white/60 text-sm mt-1">Y-tunnus: 3590217-8</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <div className="flex items-start space-x-4">
                  <Mail className="w-8 h-8 text-teal flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-white mb-2">Sähköposti</h3>
                    <a 
                      href="mailto:agencive.ai@gmail.com"
                      className="text-teal hover:text-teal-light transition-colors"
                    >
                      agencive.ai@gmail.com
                    </a>
                    <p className="text-white/60 text-sm mt-1">Palaamme sinulle mahdollisimman pian.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="w-8 h-8 text-teal flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-white mb-2">Sijainti</h3>
                    <p className="text-white/70">Lempäälä, Suomi</p>
                    <p className="text-white/60 text-sm mt-1">Palvelemme koko Suomen alueella</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="mt-8 bg-teal/10 border border-teal/30 rounded-2xl p-6">
              <h3 className="font-semibold text-white mb-3">Mitä seuraavaksi?</h3>
              <ol className="space-y-2 text-white/70 text-sm">
                <li className="flex items-start space-x-2">
                  <span className="text-teal font-bold">1.</span>
                  <span>Lähetä meille viesti tai soita</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-teal font-bold">2.</span>
                  <span>Sovitaan maksuton keskustelu tai kartoitus</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-teal font-bold">3.</span>
                  <span>Suunnitellaan yhdessä ratkaisu tarpeisiisi</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-teal font-bold">4.</span>
                  <span>Toteutetaan projekti ja ollaan tukena koko matkan</span>
                </li>
              </ol>
            </div>
          </div>

          {/* Why Contact Us */}
          <div>
            <h2 className="text-3xl font-bold mb-8 text-white">Miksi ottaa yhteyttä?</h2>
            
            <div className="space-y-6">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <h3 className="text-xl font-semibold mb-3 text-white">Maksuton kartoituskeskustelu</h3>
                <p className="text-white/70 leading-relaxed">
                  Aloitetaan 20 minuutin kartoituksella. Käydään läpi tavoitteet, brändipersoonan
                  mahdollinen käyttötapa ja realistinen aikataulu. Keskustelu on sitoutumaton
                  ja auttaa sinua ymmärtämään, sopiiko brändiagentti yrityksellesi.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <h3 className="text-xl font-semibold mb-3 text-white">Räätälöity toteutus</h3>
                <p className="text-white/70 leading-relaxed">
                  Jokainen brändiagentti suunnitellaan asiakkaan omista lähtökohdista.
                  Sovimme etukäteen, mitkä ominaisuudet ja integraatiot kuuluvat toimitukseen —
                  ei valmispaketteja, ei yleisiä lupauksia.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <h3 className="text-xl font-semibold mb-3 text-white">Suomalainen tiimi</h3>
                <p className="text-white/70 leading-relaxed">
                  Toimimme Pirkanmaalta käsin, mutta palvelemme asiakkaita etäyhteydellä
                  kaikkialla Suomessa.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <h3 className="text-xl font-semibold mb-3 text-white">Jatkokehitys sovitusti</h3>
                <p className="text-white/70 leading-relaxed">
                  Kun brändiagentti on käytössä, voidaan tarpeen mukaan sopia ylläpidosta,
                  sisältöjen päivityksestä tai lisäominaisuuksista. Laajuus sovitaan aina yhdessä.
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Additional Content for SEO */}
      <section className="py-16 px-6 bg-white/5">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6 text-white" data-testid="yhteys-faq-heading">Usein kysytyt kysymykset yhteydenotosta</h2>

          <div className="space-y-6 text-white/70">
            <div>
              <h3 className="text-xl font-semibold text-white mb-2">Miten yhteydenotto käsitellään?</h3>
              <p className="leading-relaxed">
                Vastaamme yhteydenottoihin arkisin. Kerromme, miten voisimme auttaa, ja
                ehdotamme lyhyen kartoituskeskustelun sopivaan aikaan.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-2">Onko kartoituskeskustelu maksullinen?</h3>
              <p className="leading-relaxed">
                Ensimmäinen 20 minuutin kartoituskeskustelu on maksuton ja sitoutumaton.
                Varsinainen projekti hinnoitellaan aina erikseen laajuuden mukaan.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-2">Kenelle brändiagentti sopii?</h3>
              <p className="leading-relaxed">
                Brändiagentti sopii yrityksille, jotka haluavat yhtenäisen brändipersoonan
                somesisältöihin ja verkkosivun AI-agentin. Käymme läpi käyttötapauksesi ennen
                sopimista.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-2">Miten tapaaminen järjestetään?</h3>
              <p className="leading-relaxed">
                Tapaamiset hoidetaan pääasiassa etäyhteydellä (video). Pirkanmaan alueella
                voidaan tarvittaessa tavata paikan päällä sovittaessa.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-2">Mitä tietoja kannattaa kertoa?</h3>
              <p className="leading-relaxed">
                Kerro lyhyesti yrityksestäsi, toimialasta ja mihin haluat tekoälyä käyttää.
                Jos idea ei ole vielä selkeä, autamme sen jäsentämisessä keskustelun aikana.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <a
              href="mailto:agencive.ai@gmail.com"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-teal hover:bg-teal-light text-navy font-bold rounded-xl transition-all duration-300"
            >
              <Mail className="w-5 h-5" />
              <span>Lähetä sähköpostia</span>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <ContactSection />
    </main>
  );
};

export default Yhteys;
